import React, { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "./ui/input";
import { Package } from "lucide-react";

/**
 * Combobox de recherche pour sélectionner un lot dans une liste longue.
 *
 * Filtre simultanément sur : numéro de lot, nom du produit, nom du dépôt.
 * Tri par pertinence (préfixe d'abord, puis contient) — limité à 30 résultats.
 *
 * Props :
 * - value: string (lot.id sélectionné)
 * - onChange: (lotId, lotObj) => void
 * - lots: array — liste filtrée (typiquement `lots.filter((l) => l.quantity > 0)`)
 * - productMap: { [product_id]: { name, unit } }
 * - depotFromId: (distributor_id) => string — fonction de résolution dépôt → libellé
 * - fmtNum: (qty, unit) => string — formatage quantité
 * - placeholder: string
 * - testid: string
 */
export default function LotCombobox({
  value,
  onChange,
  lots = [],
  productMap = {},
  depotFromId = () => "—",
  fmtNum = (n) => String(n),
  placeholder = "Rechercher un lot, un produit ou un dépôt…",
  testid = "lot-combobox",
}) {
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Récupère le label du lot actuellement sélectionné (pour affichage quand le combobox est fermé)
  const selectedLot = useMemo(
    () => lots.find((l) => l.id === value),
    [lots, value]
  );

  const selectedLabel = selectedLot
    ? `Lot ${selectedLot.lot_number} · ${productMap[selectedLot.product_id]?.name || "—"}`
    : "";

  const lower = (query || "").trim().toLowerCase();

  // Tri par pertinence : lot_number startsWith > product startsWith > contains
  const filtered = useMemo(() => {
    if (!focused) return [];
    if (!lower) return lots.slice(0, 30);
    const starts = [];
    const contains = [];
    for (const l of lots) {
      const lotN = (l.lot_number || "").toLowerCase();
      const prodN = (productMap[l.product_id]?.name || "").toLowerCase();
      const depN = (depotFromId(l.distributor_id) || "").toLowerCase();
      if (lotN.startsWith(lower) || prodN.startsWith(lower)) {
        starts.push(l);
      } else if (lotN.includes(lower) || prodN.includes(lower) || depN.includes(lower)) {
        contains.push(l);
      }
    }
    return [...starts, ...contains].slice(0, 30);
  }, [lots, lower, focused, productMap, depotFromId]);

  useEffect(() => {
    setHighlightIdx(-1);
  }, [filtered.length]);

  // Fermer au clic extérieur
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setFocused(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const selectLot = (l) => {
    onChange(l.id, l);
    setFocused(false);
    setQuery("");
    inputRef.current?.blur();
  };

  const onKeyDown = (e) => {
    if (!focused) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx((i) => (i + 1) % Math.max(1, filtered.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx(
        (i) => (i - 1 + filtered.length) % Math.max(1, filtered.length)
      );
    } else if (e.key === "Enter") {
      if (highlightIdx >= 0 && highlightIdx < filtered.length) {
        e.preventDefault();
        selectLot(filtered[highlightIdx]);
      }
    } else if (e.key === "Escape") {
      setFocused(false);
      setQuery("");
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Package className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A3A6A3] pointer-events-none" />
        <Input
          ref={inputRef}
          value={focused ? query : selectedLabel}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => {
            setFocused(true);
            setQuery("");
          }}
          onKeyDown={onKeyDown}
          placeholder={selectedLot ? selectedLabel : placeholder}
          className="h-9 pl-8 text-sm"
          data-testid={testid}
          autoComplete="off"
        />
      </div>
      {focused && filtered.length > 0 && (
        <div
          className="absolute z-50 left-0 right-0 top-full mt-0.5 bg-white border border-[#E5E5E0] rounded shadow-lg max-h-72 overflow-y-auto"
          data-testid={`${testid}-menu`}
        >
          {filtered.map((l, idx) => {
            const p = productMap[l.product_id];
            return (
              <button
                key={l.id}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectLot(l);
                }}
                onMouseEnter={() => setHighlightIdx(idx)}
                className={`w-full text-left px-2.5 py-1.5 text-xs border-b border-[#F0F0EB] last:border-b-0 ${
                  highlightIdx === idx ? "bg-[#F4FAF5]" : "hover:bg-[#FAFAF7]"
                }`}
              >
                <div className="font-medium text-[#1A1C1A] flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-[10px] text-[#8C6B2F] bg-[#FBF4E9] border border-[#E8D3A8] px-1.5 py-0.5 rounded">
                    {l.lot_number}
                  </span>
                  <span className="truncate">{p?.name || "—"}</span>
                </div>
                <div className="text-[10px] text-[#575A57] flex items-center gap-2 mt-0.5">
                  <span className="font-mono">{fmtNum(l.quantity, p?.unit)}</span>
                  <span>·</span>
                  <span>{depotFromId(l.distributor_id)}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
      {focused && filtered.length === 0 && lower && (
        <div className="absolute z-50 left-0 right-0 top-full mt-0.5 bg-white border border-[#E5E5E0] rounded shadow-lg px-3 py-2 text-xs text-[#A3A6A3]">
          Aucun lot ne correspond à « {query} »
        </div>
      )}
    </div>
  );
}
